import { Capacitor } from "@capacitor/core";
import styled from "@emotion/styled";
import { Button } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import DesktopZone from "../../../../../../components/Zones/DesktopZone";
import MobileZone from "../../../../../../components/Zones/MobileZone";
import { i18n } from "../../../../../../i18n/i18nText";
import { isMobile } from "../../../../../../mobileConfig";
import Store from "../../../../../../redux/index";
import {
  AnnouncementApiResponse,
  AnnouncementViewModel,
  getLocalizedAnnouncementText,
} from "./types";

const DarkBackground = styled(motion.div)`
  position: fixed;
  z-index: 9998;

  overflow: hidden;

  width: 100vw;
  height: 100vh;

  background-color: rgba(0, 0, 0, 0.5);
`;

const AnnouncementModalMobile = styled(motion.div)<{
  $migrationLayout?: boolean;
}>`
  position: fixed;
  bottom: 0;
  left: 0;

  margin-bottom: ${(props) => (props.$migrationLayout ? "0" : "-5vh")};

  width: 100vw;
  height: ${(props) => (props.$migrationLayout ? "85vh" : "80vh")};
  max-height: ${(props) => (props.$migrationLayout ? "90vh" : "80vh")};
  z-index: 99998;

  display: ${(props) => (props.$migrationLayout ? "flex" : "block")};
  flex-direction: column;
  overflow: hidden;

  background-color: white;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
`;

const MobileMigrationScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const MigrationFooter = styled.div`
  flex-shrink: 0;
  padding: 20px 24px 24px;
  background-color: #fff;
`;

const MobileMigrationFooter = styled(MigrationFooter)`
  padding: 16px 24px calc(24px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #f0f0f0;
`;

const AnnouncementModalDesktop = styled(motion.div)<{
  $migrationLayout?: boolean;
}>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 99999;
  width: ${(props) => (props.$migrationLayout ? "480px" : "600px")};
  max-width: calc(100vw - 32px);
  max-height: ${(props) => (props.$migrationLayout ? "90vh" : "none")};
  background-color: #fff;
  border-radius: ${(props) => (props.$migrationLayout ? "16px" : "0")};
  overflow: ${(props) => (props.$migrationLayout ? "hidden" : "visible")};
  box-shadow: ${(props) =>
    props.$migrationLayout ? "0 12px 40px rgba(0, 0, 0, 0.18)" : "none"};
  display: ${(props) => (props.$migrationLayout ? "flex" : "block")};
  flex-direction: column;
`;

const MigrationScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const migrationDescriptionStyles = `
  width: 100%;

  .announcement-migration {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .announcement-migration__badge {
    margin: 0;
    padding: 14px 24px 16px;
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
  }

  .announcement-migration__content {
    padding: 20px 24px 0;
  }

  .announcement-migration--shutdown .announcement-migration__badge {
    background-color: #fce8e8;
    color: #9b1c1c;
  }

  .announcement-migration--launch .announcement-migration__badge {
    background-color: #e6f4ea;
    color: #1b6b3a;
  }

  .announcement-migration__title {
    margin: 0 0 12px;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.35;
    color: #1a1c21;
  }

  .announcement-migration__text {
    margin: 0 0 10px;
    font-size: 14px;
    line-height: 1.45;
    color: #4a4a4a;
  }

  .announcement-migration__text:last-of-type {
    margin-bottom: 14px;
  }

  .announcement-migration__highlight {
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    margin: 4px 0 0;
  }

  .announcement-migration--shutdown .announcement-migration__highlight {
    background-color: #fce8e8;
    color: #9b1c1c;
  }

  .announcement-migration--launch .announcement-migration__highlight {
    background-color: #e6f4ea;
    color: #1b6b3a;
  }

  p {
    margin: 0 0 8px;
    line-height: 1.45;
    font-size: 14px;
    color: #4a4a4a;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const MigrationDescription = styled.div`
  width: 100%;
  line-height: 1.45;
  font-size: 14px;
  color: #4a4a4a;
  ${migrationDescriptionStyles}
`;

const DesktopDescription = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  width: 100%;
  line-height: 1.45;
  font-size: 14px;
  color: #4a4a4a;
  ${migrationDescriptionStyles}
`;

const AnnouncementContainer = styled.div<{ $migrationLayout?: boolean }>`
  max-height: calc(80vh - 90px);
  width: 100%;
  display: flex;

  padding: ${(props) =>
    props.$migrationLayout ? "0 0 24px" : "0 40px 25px 40px"};

  font-size: 14px;
  text-align: left;
  word-break: break-word;

  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;

  @media only screen and (max-width: 1024px) {
    height: auto;
  }
`;

const MobileTopBar = styled.div`
  align-self: center;
  margin: 10px auto 8px;
  width: 50px;
  height: 4px;
  background-color: #e0dfe4;
  border-radius: 2px;
  flex-shrink: 0;
`;

const IconContainer = styled.span`
  vertical-align: middle;
  margin-left: -20px;
  margin-right: 15px;
`;

const AnnouncementDescriptionMobile = styled.div`
  overflow-y: auto;
  margin: 0;
  width: 100%;
  line-height: 1.45;
  font-size: 14px;
  color: #4a4a4a;
  ${migrationDescriptionStyles}

  ::-webkit-scrollbar {
    -webkit-appearance: none;
  }

  ::-webkit-scrollbar:vertical {
    width: 10px;
  }

  ::-webkit-scrollbar:horizontal {
    height: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.4);
    border-radius: 10px;
    border: 2px solid #ffffff;
  }

  ::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: #ffffff;
  }

  @media (max-width: 350px) {
    max-height: 250px;
  }
`;

const BACKDROP_VARIANTS = {
  hidden: {
    display: "none",
  },
  visible: {
    display: "block",
  },
};

const ANNOUNCEMENT_MODAL_VARIANTS_MOBILE = {
  hidden: {
    y: "80vh",
  },
  visible: {
    y: 0,
  },
};

const ANNOUNCEMENT_MODAL_VARIANTS_DESKTOP = {
  hidden: {
    display: "none",
    y: "-200%",
    x: "-50%",
  },
  visible: {
    display: "block",
    y: "-50%",
    x: "-50%",
  },
};

const MigrationActions = styled.div`
  padding: 0;
  width: 100%;
  box-sizing: border-box;
`;

const CloseMigrationButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-transform: none;
`;

interface Props {
  notification: AnnouncementApiResponse;
  acceptedTerms: boolean;
  policyInfos: unknown;
}

function mapNotificationToAnnouncement(
  notification: AnnouncementApiResponse,
  language: string
): AnnouncementViewModel {
  const localizedText = getLocalizedAnnouncementText(notification, language);

  return {
    uuid: notification.uuid,
    description: localizedText.description,
    priority: notification.priority,
    button_text: localizedText.button_text,
    button2_text: localizedText.button2_text ?? "",
    link: notification.link ?? "",
  };
}

function isMigrationAnnouncement(description: string): boolean {
  return description.includes("announcement-migration");
}

function getMigrationVariant(
  description: string
): "launch" | "shutdown" | null {
  if (description.includes("announcement-migration--launch")) return "launch";
  if (description.includes("announcement-migration--shutdown"))
    return "shutdown";
  return null;
}

function getLoggedUserId(): number | null {
  const state = Store().store.getState();

  if (!state.auth.logged_in) {
    return null;
  }

  const userId = state.userID.userID;

  if (!userId || userId < 1) {
    return null;
  }

  return userId;
}

export default function Announcements(props: Props) {
  const { notification } = props;
  const lastAnnouncementUUID = localStorage.getItem("lastAnnouncementUUID");

  const [announcement, setAnnouncement] = useState<AnnouncementViewModel>();
  const [animation, setAnimation] = useState("hidden");
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (!notification) return;

    const currentLanguage = Store().store.getState().setting.language;

    dismissedRef.current = false;
    setAnnouncement(
      mapNotificationToAnnouncement(notification, currentLanguage)
    );
    setAnimation("visible");
  }, [notification]);

  const PRIORITY_MAP = {
    default: { icon: <CheckCircleIcon />, color: "#44b86a" },
    medium: { icon: <WarningIcon />, color: "rgb(255, 215, 0)" },
    high: { icon: <ErrorIcon />, color: "rgb(233, 36, 0)" },
  };

  useEffect(() => {
    if (animation === "hidden") {
      document.body.style.overflow = "initial";
    } else {
      document.body.style.overflow = "hidden";
    }
  }, [animation]);

  const handleDismissClick = () => {
    dismissedRef.current = true;
    setAnimation("hidden");
  };

  const handleOkClick = async () => {
    const isNewVersion =
      announcement != null &&
      (isMigrationAnnouncement(announcement.description) ||
        announcement.button2_text.trim() !== "");

    if (announcement?.link) {
      window.open(announcement.link);
    }

    if (isNewVersion) {
      dismissedRef.current = true;
    }

    if (!isNewVersion && announcement?.uuid) {
      localStorage.setItem("lastAnnouncementUUID", String(announcement.uuid));
    }

    setAnimation("hidden");
  };

  const renderCloseButton = () =>
    announcement?.button2_text?.trim() !== "" ? (
      <CloseMigrationButton type="button" onClick={handleDismissClick}>
        {announcement.button2_text}
      </CloseMigrationButton>
    ) : null;

  useEffect(() => {
    const announcementElement = document.getElementById("announcement");
    if (announcementElement) {
      announcementElement.innerHTML = announcement?.description ?? "";
    }
  }, [announcement?.description, isMobile()]);

  const isNewVersionAnnouncementView =
    announcement != null &&
    (isMigrationAnnouncement(announcement.description) ||
      announcement.button2_text.trim() !== "");

  if (!announcement) {
    return null;
  }

  if (isNewVersionAnnouncementView) {
    if (getLoggedUserId() == null) {
      return null;
    }
  } else if (lastAnnouncementUUID === notification?.uuid) {
    return null;
  }

  const priorityStyle =
    PRIORITY_MAP[announcement.priority] ?? PRIORITY_MAP.default;
  const isMigrationLayout = isNewVersionAnnouncementView;
  const migrationVariant = getMigrationVariant(announcement.description);
  const isMigrationLaunch = migrationVariant === "launch";

  const primaryButtonStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 16px",
    backgroundColor: isMigrationLayout
      ? isMigrationLaunch
        ? "#1B6B3A"
        : "#9B1C1C"
      : priorityStyle.color,
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    textTransform: "none",
    borderRadius: isMigrationLayout ? 10 : 4,
    boxShadow: "none",
  };

  const renderActions = (mobileFooter = false) => (
    <MigrationActions>
      <Button
        fullWidth
        variant="contained"
        color="secondary"
        onClick={handleOkClick}
        style={primaryButtonStyle}
      >
        {announcement.button_text !== ""
          ? announcement.button_text
          : i18n("FORGET_PASSWORD_OK")}
      </Button>
      {renderCloseButton()}
    </MigrationActions>
  );

  const renderLegacyMobileActions = () => (
    <MigrationActions
      style={{
        position: "fixed",
        bottom: 60,
        left: 0,
        right: 0,
        padding: "0 24px",
      }}
    >
      <Button
        fullWidth
        variant="contained"
        color="secondary"
        onClick={handleOkClick}
        style={primaryButtonStyle}
      >
        {announcement.button_text !== ""
          ? announcement.button_text
          : i18n("FORGET_PASSWORD_OK")}
      </Button>
      {renderCloseButton()}
    </MigrationActions>
  );

  return (
    <>
      <MobileZone>
        <AnnouncementModalMobile
          onMouseEnter={() => {
            if (!Capacitor.isNativePlatform()) {
              document.body.style.overflow = "hidden";
            }
          }}
          initial="hidden"
          animate={animation}
          variants={ANNOUNCEMENT_MODAL_VARIANTS_MOBILE}
          $migrationLayout={isMigrationLayout}
        >
          {isMigrationLayout ? (
            <>
              <MobileTopBar />
              <MobileMigrationScroll>
                <MigrationDescription id="announcement" />
              </MobileMigrationScroll>
              <MobileMigrationFooter>{renderActions()}</MobileMigrationFooter>
            </>
          ) : (
            <AnnouncementContainer>
              <MobileTopBar />
              <h2
                style={{
                  display: "inline-block",
                  margin: "12px 0 8px",
                  color: priorityStyle.color,
                }}
              >
                <IconContainer>{priorityStyle.icon}</IconContainer>
                {i18n("ANNOUNCEMENT_SYSTEM_TITLE")}
              </h2>
              <AnnouncementDescriptionMobile id="announcement" />
              {renderLegacyMobileActions()}
            </AnnouncementContainer>
          )}
        </AnnouncementModalMobile>
        <DarkBackground
          onScroll={(e) => {
            e.preventDefault();
          }}
          initial="hidden"
          animate={animation}
          variants={BACKDROP_VARIANTS}
        />
      </MobileZone>

      <DesktopZone>
        <AnnouncementModalDesktop
          initial="hidden"
          animate={animation}
          variants={ANNOUNCEMENT_MODAL_VARIANTS_DESKTOP}
          $migrationLayout={isMigrationLayout}
        >
          {isMigrationLayout ? (
            <>
              <MigrationScrollArea>
                <MigrationDescription id="announcement" />
              </MigrationScrollArea>
              <MigrationFooter>{renderActions()}</MigrationFooter>
            </>
          ) : (
            <AnnouncementContainer>
              <h1
                style={{
                  paddingTop: 10,
                  display: "inline-block",
                  margin: "0 0 8px",
                  color: priorityStyle.color,
                  textAlign: "center",
                }}
              >
                <IconContainer>{priorityStyle.icon}</IconContainer>
                {i18n("ANNOUNCEMENT_SYSTEM_TITLE")}
              </h1>
              <DesktopDescription id="announcement" />
              {renderActions()}
            </AnnouncementContainer>
          )}
        </AnnouncementModalDesktop>
        <DarkBackground
          onScroll={(e) => {
            e.preventDefault();
          }}
          initial="hidden"
          animate={animation}
          variants={BACKDROP_VARIANTS}
        />
      </DesktopZone>
    </>
  );
}
